import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import DynamicRows from "../../../../components/dynamic-rows-cards";
import TextArea from "../../../../components/forms/text-area";
import TextBox from "../../../../components/forms/text-box";
import ActionButtons from "../../common/components/buttons-settings";
import DisplayTexts from "../../common/components/display-texts";
import SettingTip from "../../common/components/setting-tip";
import { WORKFLOW_OPTION_SLOT } from "../../common/const/settings-const";
import { QUESTION_PROMPT } from "../../common/const/settings-type";
import { StmStateDto } from "../../common/dto/stm-state-dto";
import CardImageUrl from "./card-image-url";
import isEqual from "lodash/isEqual";
import SlotSelector from "../../common/components/slot-selector";
import MultiLang from "../../common/components/multi-lang";

// Create a class component for Card to better handle focus retention
class Card extends React.Component {
  constructor(props) {
    super(props);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleButtonsChange = this.handleButtonsChange.bind(this);
  }
  
  shouldComponentUpdate(nextProps) {
    // Only update if cardConfig has actually changed
    return !isEqual(this.props.cardConfig, nextProps.cardConfig);
  }
  
  handleInputChange(event) {
    this.props.handleInputChange(event);
  }
  
  handleButtonsChange(rowsData) {
    this.props.handleInputChange({
      target: {
        name: 'buttons',
        value: rowsData
      }
    });
  }
  
  render() {
    const { cardConfig, rawId, metadata } = this.props;
    
    return (
      <div className="flex flex-col gap-2" key={rawId}>
        <CardImageUrl config={cardConfig} onChange={this.handleInputChange} />
        <TextBox
          rootClass=""
          label="* Title"
          type="text"
          name="title"
          placeholder="Enter Title"
          value={cardConfig.title || ""}
          onChange={this.handleInputChange}
        />
        <MultiLang
          rootClassName="pt-2"
          value={cardConfig.lang_title || {}}
          name="lang_title"
          responseModifyByAI={false}
          onChangeData={this.handleInputChange}
          showBottomDivider={true}
        />
        <TextArea
          rootClass="w-full"
          label="Description"
          type="text"
          name="description"
          placeholder="Enter Description"
          value={cardConfig.description || ""}
          onChange={this.handleInputChange}
        />
        <MultiLang
          title="Description MultiLang"
          rootClassName="pt-2"
          value={cardConfig.lang_description || {}}
          name="lang_description"
          responseModifyByAI={false}
          onChangeData={this.handleInputChange}
          showBottomDivider={true}
        />
        {/* <TextBox
          rootClass=""
          label="* Payload"
          type="text"
          name="payload"
          placeholder="Enter Payload"
          value={cardConfig.payload || ""}
          onChange={this.handleInputChange}
        /> */}
        <ActionButtons
          dataField='buttons'
          configs={cardConfig['buttons'] || []}
          metadata={metadata}
          onChange={this.handleButtonsChange}
        />
      </div>
    );
  }
}

function Page({ name, onChangeData, savedSettings, metadata }) {
  const [formData, setFormData] = useState(savedSettings);
  const [cards, setCards] = useState([]);

  const notifyOnchangeData = data => {
    data.slot = data.slot || WORKFLOW_OPTION_SLOT;
    data.stateType = QUESTION_PROMPT
    onChangeData(data);
  }

  useEffect(() => {
    if (savedSettings && !isEqual(savedSettings.data, cards)) {
      setCards(savedSettings.data)
    }
  }, [savedSettings]);

  useEffect(() => {
    notifyOnchangeData(formData);
  }, [formData]);

  const handleChange = useCallback(event => {
    const { name, value } = event.target;

    setFormData(prevFormData => {
      // if (prevFormData[name] === value) return prevFormData;
      return {
        ...prevFormData,
        [name]: value
      };
    });
  }, []);

  const prevCards = useRef(cards);

  useEffect(() => {
    if (!isEqual(cards, prevCards.current)) {
      console.log('cards', cards)
      console.log('prevCards.current', prevCards.current)
      handleChange({
        target:{
          name: 'data',
          value: cards
        }
      })
      prevCards.current = cards;
    }
  }, [cards]);

  const prepareRawsData = useCallback((data = []) => {
    return data.map(header => ({ inputs: header }))
  }, [])

  const onDynamicRowChange = useCallback((rowsData) => {
    rowsData = rowsData.map(raw => raw.inputs)
    rowsData = rowsData.map(raw => {
      // raw.thumbnail = raw.title;
      raw.buttons = raw.buttons || [];
      return raw;
    })
    setCards(rowsData)
  }, [])

  const handleFallbackButtonsChange = useCallback((rowsData) => {
    handleChange({
      target: {
        name: 'fallbackActions',
        value: rowsData
      }
    })
  }, [handleChange])

  const memoizedDisplayTextOptions = useMemo(() => formData.displayTextOptions, [formData.displayTextOptions]);
  const memoizedRawData = useMemo(() => prepareRawsData(formData.data), [formData]);
  
  // Use a ref to store input change handlers for each row to prevent re-creation on render
  const inputChangeHandlers = useRef(new Map()).current;
  
  const renderRow = useCallback((cardConfig, handleInputChange, rawId) => {
    cardConfig.buttons = cardConfig?.buttons || [];
    
    // Create a stable input change handler for this specific row if it doesn't exist
    if (!inputChangeHandlers.has(rawId)) {
      inputChangeHandlers.set(rawId, (event) => {
        const { name, value } = event.target;
        
        // Apply the change directly to cardConfig to maintain focus
        cardConfig[name] = value;
        
        // Then notify about the change
        handleInputChange(event);
      });
    }
    
    // Use the stable handler from our map
    const stableInputChangeHandler = inputChangeHandlers.get(rawId);
    
    return (
      <Card 
        key={rawId} 
        rawId={rawId} 
        cardConfig={cardConfig} 
        handleInputChange={stableInputChangeHandler} 
        metadata={metadata} 
      />
    );
  }, [metadata, inputChangeHandlers]);

  return (
    <div className="flex flex-col gap-2">
      <SettingTip text="Display a collection of visually organised cards, presenting information or options" />
      <TextBox
        label="Name"
        type="text"
        name="name"
        placeholder="State name"
        value={formData.name}
        onChange={handleChange}
      />
        {/* <SlotSelector
            title="Variable"
            value={formData.slot}
            autoFillFromEntity={formData.autoFillFromEntity}
            slots={metadata.slots}
            onChange={handleChange}
        /> */}
      <DisplayTexts
        parameterName={formData.slot}
        displayTextOptionsProps={memoizedDisplayTextOptions}
        responseModifyByAI={formData.responseModifyByAI}
        onChangeData={handleChange}
      />
      <MultiLang
        name="lang_displayTextOptions"
        rootClassName="pt-2"
        value={formData.lang_displayTextOptions || {}}
        parameterName={formData.slot}
        responseModifyByAI={formData.responseModifyByAI}
        onChangeData={handleChange}
        showBottomDivider={false}
      />
      <DynamicRows
        title="Cards"
        className="pt-2"
        rowsData={memoizedRawData}
        onChange={onDynamicRowChange}
        showBottomDivider={true}
        defaultThumbnail="Card"
        rowsTemplate={renderRow}
      />
      <div className="pt-4">
        <div className="text-sm font-medium text-gray-700">Fallback action configs</div>
        <TextArea
          rootClass="w-full pt-2"
          label="Description"
          type="text"
          name="fallbackDescription"
          placeholder="Enter Description"
          value={formData.fallbackDescription || ""}
          onChange={handleChange}
        />
        <MultiLang
          rootClassName="pt-2"
          name="lang_fallbackDescription"
          value={formData.lang_fallbackDescription || {}}
          parameterName="lang_fallbackDescription"
          onChangeData={handleChange}
          showBottomDivider={true}
        />
        <ActionButtons
          dataField='fallbackActions'
          configs={formData['fallbackActions'] || []}
          metadata={metadata}
          onChange={handleFallbackButtonsChange}
          displayTopBorder={false}
        />
      </div>
    </div>
  )
}

export default Page
